// src/components/ui/HintSystem.tsx
/**
 * Hint System Component
 *
 * Beautiful UI for displaying combination hints to players
 */

import React, { useState, useEffect } from 'react';
import { Lightbulb, X, Sparkles, ChevronRight, Play, Gift } from 'lucide-react';
import { colors } from '../../theme/designTokens';
import {
  getRandomHint,
  getHintCount,
  Hint,
  getTotalHintsAvailable,
  consumeHint,
  rewardHintsForAd,
  getHintCreditBreakdown,
  hasHintsAvailable
} from '../../services/hintService';
import { triggerHaptic } from '../../services/capacitorService';

interface HintSystemProps {
  unlockedElements: string[];
}

const HintSystem: React.FC<HintSystemProps> = ({ unlockedElements }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentHint, setCurrentHint] = useState<Hint | null>(null);
  const [showingHint, setShowingHint] = useState(false);
  const [creditsAvailable, setCreditsAvailable] = useState(getTotalHintsAvailable());
  const [showAdReward, setShowAdReward] = useState(false);

  const availableHints = getHintCount(unlockedElements);

  // Update credits when component mounts or when hints are used
  useEffect(() => {
    setCreditsAvailable(getTotalHintsAvailable());
  }, [isOpen]);

  const handleRequestHint = () => {
    triggerHaptic('light');

    // Check if user has credits available
    if (!hasHintsAvailable()) {
      // Show modal with "Watch Ad" option
      setShowAdReward(true);
      setIsOpen(true);
      return;
    }

    // Check if there are hints available for current unlocked elements
    const hint = getRandomHint(unlockedElements);
    if (!hint) {
      triggerHaptic('error');
      return;
    }

    // Deduct a hint credit
    const success = consumeHint();
    if (success) {
      setCurrentHint(hint);
      setShowingHint(true);
      setIsOpen(true);
      setShowAdReward(false);
      setCreditsAvailable(getTotalHintsAvailable());
    }
  };

  const handleWatchAd = () => {
    triggerHaptic('success');
    // TODO: Integrate with actual ad network
    // For now, just reward the hints
    const hintsRewarded = rewardHintsForAd();
    setCreditsAvailable(getTotalHintsAvailable());
    setShowAdReward(false);

    // Show success message
    alert(`You earned ${hintsRewarded} hints! Total: ${getTotalHintsAvailable()}`);
  };

  const handleClose = () => {
    triggerHaptic('light');
    setIsOpen(false);
    setTimeout(() => {
      setShowingHint(false);
      setCurrentHint(null);
      setShowAdReward(false);
    }, 300);
  };

  if (availableHints === 0) return null; // No hints available for current progress

  return (
    <>
      {/* Floating Hint Button */}
      <button
        onClick={handleRequestHint}
        style={{
          position: 'fixed',
          bottom: 'max(env(safe-area-inset-bottom), 6rem)',
          right: 'max(env(safe-area-inset-right), 1rem)',
          zIndex: 1001,
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          border: `3px solid ${colors.brand.warning}`,
          background: `linear-gradient(135deg, ${colors.brand.warning}, ${colors.brand.accent})`,
          boxShadow: `0 8px 32px ${colors.brand.warning}60`,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'float 3s ease-in-out infinite, pulse 2s ease-in-out infinite',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = `0 12px 40px ${colors.brand.warning}80`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = `0 8px 32px ${colors.brand.warning}60`;
        }}
      >
        <Lightbulb
          size={32}
          style={{
            color: 'white',
            filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.8))',
          }}
        />
        {creditsAvailable > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: colors.brand.error,
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            {creditsAvailable > 99 ? '99+' : creditsAvailable}
          </div>
        )}
      </button>

      {/* Hint Modal */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            animation: 'fadeIn 0.3s ease-out',
          }}
          onClick={handleClose}
        >
          <div
            style={{
              background: `linear-gradient(135deg, ${colors.background.card}, ${colors.background.secondary})`,
              borderRadius: '32px',
              maxWidth: '600px',
              width: '100%',
              padding: '48px 32px',
              border: `3px solid ${colors.brand.warning}`,
              boxShadow: `0 20px 60px ${colors.brand.warning}40`,
              position: 'relative',
              animation: 'scaleIn 0.4s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                color: colors.text.secondary,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X size={24} />
            </button>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${colors.brand.warning}, ${colors.brand.accent})`,
                  marginBottom: '16px',
                  boxShadow: `0 8px 32px ${colors.brand.warning}60`,
                  animation: 'pulse 2s ease-in-out infinite',
                }}
              >
                <Lightbulb
                  size={48}
                  style={{
                    color: 'white',
                    filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.8))',
                  }}
                />
              </div>
              <h2
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  background: colors.gradients.legendary,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '8px',
                }}
              >
                Alchemist's Hint
              </h2>
              <p style={{ color: colors.text.secondary, fontSize: '1rem' }}>
                A cryptic clue from the ancient texts...
              </p>
            </div>

            {/* Hint Content */}
            {showAdReward ? (
              /* Out of Credits - Watch Ad */
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '20px',
                  padding: '32px',
                  marginBottom: '24px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                }}
              >
                <Gift
                  size={64}
                  style={{
                    color: colors.brand.warning,
                    marginBottom: '16px',
                  }}
                />
                <h3
                  style={{
                    color: colors.text.primary,
                    fontSize: '1.5rem',
                    marginBottom: '12px',
                  }}
                >
                  Out of Hints!
                </h3>
                <p
                  style={{
                    color: colors.text.secondary,
                    fontSize: '1rem',
                    marginBottom: '24px',
                  }}
                >
                  Watch a short ad to earn 5 more hints
                </p>
                <button
                  onClick={handleWatchAd}
                  style={{
                    padding: '16px 32px',
                    borderRadius: '12px',
                    border: 'none',
                    background: colors.gradients.primary,
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: `0 4px 16px ${colors.brand.primary}40`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 6px 20px ${colors.brand.primary}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 16px ${colors.brand.primary}40`;
                  }}
                >
                  <Play size={20} fill="white" />
                  <span>Watch Ad</span>
                </button>
              </div>
            ) : showingHint && currentHint ? (
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '20px',
                  padding: '32px',
                  marginBottom: '24px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative sparkles */}
                <div
                  style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    animation: 'float 3s ease-in-out infinite',
                  }}
                >
                  <Sparkles size={20} style={{ color: colors.brand.warning, opacity: 0.6 }} />
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: '16px',
                    right: '16px',
                    animation: 'float 3s ease-in-out infinite 1.5s',
                  }}
                >
                  <Sparkles size={16} style={{ color: colors.brand.accent, opacity: 0.6 }} />
                </div>

                {/* Hint message */}
                <p
                  style={{
                    color: colors.text.primary,
                    fontSize: '1.3rem',
                    lineHeight: '1.8',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    marginBottom: '16px',
                  }}
                >
                  "{currentHint.message}"
                </p>

                {/* Tier badge */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                  <div
                    style={{
                      background: colors.elementTiers[currentHint.tier as keyof typeof colors.elementTiers].color + '30',
                      color: colors.elementTiers[currentHint.tier as keyof typeof colors.elementTiers].color,
                      padding: '8px 16px',
                      borderRadius: '12px',
                      fontSize: '0.9rem',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Tier {currentHint.tier} Element
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '48px 24px',
                  color: colors.text.tertiary,
                }}
              >
                <p>Getting hint...</p>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  triggerHaptic('light');
                  handleRequestHint();
                }}
                style={{
                  padding: '12px 32px',
                  borderRadius: '12px',
                  border: 'none',
                  background: colors.gradients.primary,
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: `0 4px 16px ${colors.brand.primary}40`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 6px 20px ${colors.brand.primary}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 16px ${colors.brand.primary}40`;
                }}
              >
                <span>Another Hint</span>
                <ChevronRight size={20} />
              </button>

              <button
                onClick={handleClose}
                style={{
                  padding: '12px 32px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: colors.text.primary,
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
              >
                Close
              </button>
            </div>

            {/* Hint credits footer */}
            <div
              style={{
                textAlign: 'center',
                marginTop: '24px',
                color: colors.text.tertiary,
                fontSize: '0.9rem',
              }}
            >
              {(() => {
                const breakdown = getHintCreditBreakdown();
                return (
                  <>
                    <p style={{ marginBottom: '8px' }}>
                      <strong style={{ color: colors.text.primary }}>
                        {breakdown.total}
                      </strong>{' '}
                      {breakdown.total === 1 ? 'hint' : 'hints'} available
                    </p>
                    <p style={{ fontSize: '0.8rem' }}>
                      {breakdown.dailyFree > 0 && (
                        <span style={{ color: colors.brand.success }}>
                          {breakdown.dailyFree} free daily
                        </span>
                      )}
                      {breakdown.dailyFree > 0 && breakdown.bonusCredits > 0 && (
                        <span> • </span>
                      )}
                      {breakdown.bonusCredits > 0 && (
                        <span style={{ color: colors.brand.warning }}>
                          {breakdown.bonusCredits} bonus
                        </span>
                      )}
                    </p>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 8px 32px ${colors.brand.warning}60;
            }
            50% {
              box-shadow: 0 8px 32px ${colors.brand.warning}80, 0 0 40px ${colors.brand.warning}40;
            }
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

export default HintSystem;
